import React, { useState, useEffect } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import moment from 'moment';
import runningIcon from '../../assets/icons/running.png';
import cyclingIcon from '../../assets/icons/cycling.png';
import swimIcon from '../../assets/icons/swim.png';
import strengthIcon from '../../assets/icons/strength.png';
import '../styles/ActivityDetailsModal.css';
import CyclingHeader from './CyclingHeader';
import CyclingDetailsForm from './CyclingDetailsForm';
import FileUploadComponent from '../FileUploadComponent';
import CyclingActions from './CyclingActions';
import PlannedVsCompletedCycling from './PlannedVsCompletedCycling';
import WellnessForm from '../ActivityDetails/WellnessForm';
import CyclingSummary from './CyclingSummary';
import PowerChart from './PowerChart';
import LapsTableCycling from './LapsTableCycling';
import calculateSpeedAndPace from '../../utils/calculateSpeedAndPace';
import { calculateTSSWithTerrainCycling as calculateTSS } from '../../utils/tssCyclingUtils';
import { calculateNormalizedPower } from '../../utils/normalizedPower';
import '../styles/CyclingActivityModal.css';
import ZoneDistributionChartCycling from '../AthleteDashboard/ZoneDistributionChartCycling';
import ZoneDistributionStrava from '../AthleteDashboard/ZoneDistributionStrava';
import CyclingElevationProfile from '../Cycling/CyclingElevationProfile';
import CyclingPhysiologicalAnalysis from '../Cycling/CyclingPhysiologicalAnalysis';
import GeoLocationComponentCycling from '../Cycling/GeoLocationComponentCycling';
import { calculateVO2Max } from '../../utils/vo2Utils';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';
import SessionAiSummaryPanel from '../Insights/SessionAiSummaryPanel';
import HRLapHistogramCycling from './HRLapHistogramCycling';
import StrengthHeartRateHistogram from '../Strength/StrengthHeartRateHistogram';

const initialActivityState = {
  title: '',
  description: '',
  preComments: '',
  postComments: '',
  planned: {},
  completed: {},
  metrics: {}
};

const CyclingActivityModal = ({ show, onHide, activityType, onSave, onDelete, fetchEvents, profilePicture, selectedDate }) => {
  const { t } = useTranslation();
  const [activity, setActivity]     = useState(initialActivityState);
  const [summary, setSummary]       = useState(null);
  const [rawData, setRawData]       = useState(null);
  const [tssMessage, setTssMessage] = useState(null);
  const [banister, setBanister]     = useState(null);

  const fetchParsedData = async (fileId) => {
    try {
      const res    = await fetch(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
      const parsed = await res.json();

      const session     = parsed?.sessions?.[0] || parsed?.session || {};
      const activityRaw = parsed?.activity || {};
      const zones       = parsed?.zones_target || {};
      const weight      = parsed?.athlete?.weight || 70;

      const { avgSpeed: velocidad_promedio, avgPace: ritmo_promedio } = calculateSpeedAndPace({ session, activity: activityRaw });
      const { tss, message } = calculateTSS({ ...parsed, session });
      const normalizedPower  = calculateNormalizedPower(parsed.records || []);
      const ftp   = zones.functional_threshold_power || 180;
      const rawIF = ftp > 0 ? normalizedPower / ftp : 'N/A';

      const parsedWeight = parseFloat(weight);
      const safeWeight   = parsedWeight > 30 ? parsedWeight : 70;
      const wKg = normalizedPower && safeWeight ? (normalizedPower / safeWeight).toFixed(2) : null;

      const laps = (parsed.laps || []).map((lap, index) => {
        const durationSec  = parseFloat(lap.total_timer_time ?? lap.total_elapsed_time ?? 0);
        const distance     = parseFloat(lap.total_distance ?? 0);
        const avgSpeedCalc = durationSec > 0 && distance > 0 ? (distance / durationSec * 3.6).toFixed(2) : '-';
        return {
          vuelta:                    index + 1,
          duracion:                  new Date(durationSec * 1000).toISOString().substr(11, 8),
          tiempoAcumulado:           '-',
          distancia:                 (distance / 1000).toFixed(2),
          calorias:                  lap.total_calories ?? '-',
          ascenso:                   lap.total_ascent ?? '-',
          descenso:                  lap.total_descent ?? '-',
          frecuenciaCardiacaPromedio: lap.avg_heart_rate ?? '-',
          frecuenciaCardiacaMaxima:   lap.max_heart_rate ?? '-',
          velocidadPromedio:          avgSpeedCalc,
          velocidadMaxima:            lap.max_speed ? (lap.max_speed * 3.6).toFixed(2) : '-',
          avg_speed:                  lap.avg_speed ?? null,
          max_speed:                  lap.max_speed ?? null,
          cadenciaPromedio:           lap.avg_cadence ?? '-',
          temperaturaPromedio:        lap.avg_temperature ?? '-',
          temperaturaMaxima:          lap.max_temperature ?? '-',
          avg_power:                  lap.avg_power ?? null,
          max_power:                  lap.max_power ?? null,
          normalizedPower:            lap.normalized_power ?? null,
          wKg:                        lap.avg_power ? (lap.avg_power / safeWeight).toFixed(2) : '0.00',
          start_time:                 lap.start_time || session?.start_time || activityRaw?.start_time || null
        };
      });

      let vo2maxFinal = session.vo2max || parsed.vo2max;
      if (!vo2maxFinal || vo2maxFinal === 'N/A') {
        const distanceKm  = (session?.total_distance || activityRaw?.total_distance || 0) / 1000;
        const durationSec = session?.total_timer_time || activityRaw?.total_timer_time || 0;
        vo2maxFinal = calculateVO2Max(distanceKm, durationSec);
        console.log("🧮 VO2max calculado manualmente →", vo2maxFinal);
      }

      const completed = {
        ...parsed.completed,
        fileId,
        velocidad_promedio,
        ritmo_promedio,
        tss,
        vo2max:          vo2maxFinal,
        normalizedPower,
        intensityFactor: rawIF,
        ftp,
        maxSpeed:        session.max_speed || 0,
        wKg,
        duracion:        session.total_timer_time || activityRaw.total_timer_time || 0,
        totalAscent:     session.total_ascent || activityRaw.total_ascent || 0
      };

      setActivity((prev) => ({ ...prev, completed, metrics: parsed.metrics || {} }));
      setSummary({ completed, metrics: parsed.metrics || {} });
      setTssMessage(message);
      setRawData({ ...parsed, laps });
    } catch (error) {
      console.error('Error cargando parsedData:', error);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (!activity?.title?.trim())       return alert(t('cycling.validationTitle'));
    if (!activity?.description?.trim()) return alert(t('cycling.validationDescription'));
    const hasPlanned = Object.values(activity?.planned || {}).some(v => (v ?? '').toString().trim() !== '');
    if (!hasPlanned) return alert(t('cycling.validationPlanned'));

    const API = API_BASE_URL;
    const pad = (n) => String(n).padStart(2, '0');
    const toHHMMSS = (s) => {
      if (!s) return '';
      const m = /^\d{1,2}:\d{2}(:\d{2})?$/.exec(String(s));
      if (m) { const [h, mi, ss = '00'] = String(s).split(':'); return `${pad(h)}:${pad(mi)}:${pad(ss)}`; }
      return String(s);
    };
    const toKmString = (input) => {
      if (input == null) return '';
      let v = String(input).trim().toLowerCase().replace(',', '.');
      if (/^\d+(\.\d+)?\s*m$/.test(v)) return String(parseFloat(v) / 1000);
      const num = parseFloat(v);
      return Number.isFinite(num) ? String(num) : '';
    };

    const dur  = toHHMMSS(activity.planned?.duracion || activity.planned?.duration || '');
    const dist = toKmString(activity.planned?.distancia ?? activity.planned?.distance ?? '');
    const startIso = new Date(selectedDate || new Date()).toISOString();

    const payload = {
      ...activity,
      assignedTo: activity.assignedTo || activityType?.assignedTo || localStorage.getItem('selectedAthleteId') || localStorage.getItem('userId'),
      trainer: localStorage.getItem('trainerId') || null,
      type: (activity.type || activityType?.type || 'ciclismo'),
      date:  startIso,
      start: startIso,
      end:   startIso,
      planned: {
        ...(activity.planned || {}),
        duracion:  dur  || '',
        duration:  dur  || '',
        distancia: dist || '',
        distance:  dist || ''
      }
    };

    if (typeof onSave === 'function') {
      onSave(payload);
      onHide?.();
      return;
    }

    try {
      const res = await fetch(`${API}/api/calendar/activities`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload)
      });
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(`Error al guardar: ${res.status} ${msg}`);
      }
      await res.json();
      typeof fetchEvents === 'function' && fetchEvents();
      onHide?.();
    } catch (err) {
      console.error('❌ Cycling save error:', err);
      alert(err.message || t('cycling.saveError'));
    }
  };

  const handleFileUpload = async (data) => {
    if (data.fileId) {
      await fetchParsedData(data.fileId);
      if (typeof fetchEvents === 'function') fetchEvents();
    }
  };

  useEffect(() => {
    const athleteId = activityType?.assignedTo || localStorage.getItem('selectedAthleteId') || localStorage.getItem('userId');
    if (!athleteId) return;
    fetch(`${API_BASE_URL}/api/analytics/athlete/${athleteId}/pmc`)
      .then(r => r.json())
      .then(d => { if (d && d.ctl !== undefined) setBanister({ ctl: d.ctl, atl: d.atl, tsb: d.tsb, ramp7d: d.ramp7d }); })
      .catch(() => {});
  }, [activityType]);

  useEffect(() => {
    if (activityType) {
      setActivity({
        ...initialActivityState,
        ...activityType,
        planned:   activityType.planned   || {},
        completed: activityType.completed || {},
        metrics:   activityType.metrics   || {}
      });
      if (activityType.completed?.fileId) fetchParsedData(activityType.completed.fileId);
    }
    return () => {
      setActivity(initialActivityState);
      setSummary(null);
      setRawData(null);
      setTssMessage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityType?.id, activityType?._id, activityType?.completed?.fileId, activityType?.fileId]);

  const handleDelete = () => {
    if (typeof onDelete === 'function' && activityType?.id) {
      onDelete(activityType.id);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton className="tz20-activity-header">
        <div className="tz20-activity-title-wrap">
          <span className="tz20-activity-icon" aria-hidden="true"><img src={cyclingIcon} alt="" /></span>
          <div>
            <Modal.Title className="tz20-activity-title">
              {t('activityDetails.activityModalTitle', { title: activity.title || t('activityLabels.cycling') })}
            </Modal.Title>
            <div className="tz20-activity-meta">
              {activity?.date ? moment(activity.date).format('ddd D MMM YYYY') : moment().format('ddd D MMM YYYY')}
              {' · '}
              {activity?.completed?.fileId ? 'Archivo .fit cargado' : 'Sin archivo .fit'}
              {activity?.gearName && <span style={{ marginLeft: 6 }}>· 🚴 {activity.gearName}</span>}
            </div>
          </div>
        </div>
        <div className="tz20-sport-tabs" aria-label="Deportes">
          <button type="button" aria-label="Correr"><img src={runningIcon} alt="" /></button>
          <button type="button" className="active" aria-label="Ciclismo"><img src={cyclingIcon} alt="" /></button>
          <button type="button" aria-label="Nadar"><img src={swimIcon} alt="" /></button>
          <button type="button" aria-label="Fuerza"><img src={strengthIcon} alt="" /></button>
        </div>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-4 align-items-center">
          <CyclingHeader
            profilePicture={profilePicture}
            profileData={{ name: '', profilePicture: '' }}
            selectedDate={selectedDate}
          />
        </Row>

        <Row className="align-items-start mt-4">
          <Col md={6}>
            <CyclingDetailsForm
              title={activity.title}
              setTitle={(title) => setActivity((prev) => ({ ...prev, title }))}
              description={activity.description}
              setDescription={(description) => setActivity((prev) => ({ ...prev, description }))}
              preComments={activity.preComments}
              setPreComments={(preComments) => setActivity((prev) => ({ ...prev, preComments }))}
              postComments={activity.postComments}
              setPostComments={(postComments) => setActivity((prev) => ({ ...prev, postComments }))}
            />
          </Col>
          <Col md={6}>
            <PlannedVsCompletedCycling
              planned={activity.planned}
              completed={activity.completed}
              setPlanned={(planned) => setActivity((a) => ({ ...a, planned }))}
            />
          </Col>
        </Row>

          {!activity.completed?.fileId && (
            <WellnessForm
              athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
              activityId={activityType?.id}
              date={activity?.date || activityType?.date}
            />
          )}
        <div className="section-block mt-4">
          <FileUploadComponent
            activityId={activityType?.id || 'undefined'}
            activityType="ciclismo"
            onFileUpload={handleFileUpload}
          />
        </div>


        {activity.completed?.fileId && (
          <>
            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('cycling.activitySummary')}</h2>
              <CyclingSummary fileId={activity.completed.fileId} />
            </div>

            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('cycling.geoMap')}</h2>
              <GeoLocationComponentCycling fileId={activity.completed.fileId} />
            </div>

            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('cycling.elevationProfile')}</h2>
              <CyclingElevationProfile fileId={activity.completed.fileId} />
            </div>
          </>
        )}

        {rawData?.records?.length > 0 && (
          <div className="section-block">
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('cycling.powerChart')}</h2>
            <div style={{ width: '100%', maxWidth: 1000, height: 300, margin: '0 auto', backgroundColor: '#fff', border: '1px solid lightgray' }}>
              <PowerChart records={rawData.records} />
            </div>
          </div>
        )}


        {activity.completed?.fileId && (
          <>
            {summary?.completed && (
              <div className="section-block">
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('cycling.physiologicalAnalysis')}</h2>
                <CyclingPhysiologicalAnalysis
                  completed={summary.completed}
                  metrics={summary.metrics}
                  athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
                  activityId={activityType?.id}
                  date={activity?.date || activityType?.date}
                  banister={banister}
                />
              </div>
            )}

            <div className="section-block">
              <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('cycling.zoneDistribution')}</h2>
              {String(activity.completed?.fileId || '').startsWith('strava-')
                ? <ZoneDistributionStrava
                    userId={activity.assignedTo || activityType?.assignedTo || localStorage.getItem('userId')}
                    fileId={activity.completed.fileId}
                    sport="ciclismo"
                  />
                : <ZoneDistributionChartCycling
                    userId={activity.assignedTo || activityType?.assignedTo || localStorage.getItem('userId')}
                    fileId={activity.completed.fileId}
                  />
              }
            </div>
          </>
        )}

        {rawData?.laps?.length > 0 && (
          <div className="section-block">
            <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('cycling.lapsDetail')}</h2>
            <LapsTableCycling
              laps={rawData.laps}
              records={rawData.records}
              fileId={activity.completed?.fileId}
            />
          </div>
        )}
            {activity.completed?.fileId && (
              <div className="section-block">
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('elevationProfile.hrLapHistogramTitle')}</h2>
                <HRLapHistogramCycling fileId={activity.completed.fileId} />
              </div>
            )}
            {activity.completed?.fileId && activity.hasHeartrate !== false && !activity.isIndoor && (
              <div className="section-block">
                <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('elevationProfile.hrTimeChartTitle')}</h2>
                <StrengthHeartRateHistogram fileId={activity.completed.fileId} />
              </div>
            )}
            {activity.completed?.fileId && (
              <WellnessForm
                athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
                activityId={activityType?.id}
                date={activity?.date || activityType?.date}
              />
            )}
            <SessionAiSummaryPanel
              sport="cycling"
              athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
              activityId={activityType?.id}
              banister={banister}
              date={activity?.date || activityType?.date}
              completed={summary?.completed}
              metrics={summary?.metrics}
            />
      </Modal.Body>
      <Modal.Footer>
        <CyclingActions
          onSave={handleSave}
          onDelete={handleDelete}
          onHide={onHide}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default CyclingActivityModal;
